import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { TRUST_ITEMS } from "../data/content";

const HERO_SLIDES = [
  { src: "/images/stadium-tribune.jpeg", position: "center 35%" },
  { src: "/images/carport.jpeg", position: "center 30%" },
  { src: "/images/new-c.jpeg", position: "center 55%" },
  { src: "/images/stadium-2.jpeg", position: "center 45%" },
  { src: "/images/stairs-closeup.webp", position: "center 40%" },
  { src: "/images/roof-structure.jpeg", position: "center 40%" },
  { src: "/images/gate-3.jpeg", position: "center 60%" },
];

const fade = (delay) => ({
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const [slide, setSlide] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % HERO_SLIDES.length), 2500);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="ballina" data-testid="hero-section" className="relative flex min-h-[100svh] flex-col justify-end overflow-hidden bg-ink">
      {HERO_SLIDES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={i === 0 ? "Punime metalike nga ALBESI AXH METAL" : ""}
          aria-hidden={i !== slide}
          data-testid={i === slide ? "hero-image" : undefined}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-[900ms] ease-out ${
            i === slide ? "opacity-100" : "opacity-0"
          }`}
          style={{ objectPosition: s.position, transform: i === slide ? "scale(1.04)" : "scale(1)", transitionProperty: "opacity, transform", transitionDuration: "900ms, 2600ms" }}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/25" />
      <div className="absolute inset-0 texture-grain" />

      <div className="relative mx-auto w-full max-w-7xl px-5 pb-14 pt-36 sm:px-8 sm:pb-20">
        <motion.p {...fade(0.1)} className="eyebrow mb-5 [text-shadow:0_1px_10px_rgba(13,14,17,0.9)]" data-testid="hero-eyebrow">
          Çellopek · Tetovë · Maqedonia e Veriut
        </motion.p>
        <motion.h1
          {...fade(0.25)}
          data-testid="hero-title"
          className="max-w-4xl font-display text-4xl font-bold leading-[1.06] tracking-tight text-white [text-shadow:0_2px_24px_rgba(13,14,17,0.7)] sm:text-5xl lg:text-7xl"
        >
          ALBESI AXH METAL
          <span className="mt-4 block text-2xl font-medium leading-snug text-white/90 sm:text-3xl lg:text-4xl">
            Punime metalike me përvojë, cilësi dhe <span className="text-bronze">garanci</span>
          </span>
        </motion.h1>
        <motion.p {...fade(0.4)} data-testid="hero-subtitle" className="mt-5 max-w-xl text-base leading-relaxed text-white/70 [text-shadow:0_1px_12px_rgba(13,14,17,0.8)] sm:text-lg">
          Mbi 30 vite përvojë në përpunimin dhe realizimin e punimeve metalike.
        </motion.p>
        <motion.div {...fade(0.55)} className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-4">
          <a
            href="#kontakt"
            data-testid="hero-cta-contact"
            className="group inline-flex items-center justify-center gap-3 border border-bronze bg-bronze px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-ink transition-colors duration-300 hover:bg-transparent hover:text-bronze"
          >
            Na kontaktoni
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#punimet"
            data-testid="hero-cta-works"
            className="inline-flex items-center justify-center gap-3 border border-white/30 bg-ink/30 px-7 py-4 font-display text-sm font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-sm transition-colors duration-300 hover:border-bronze hover:text-bronze"
          >
            Shikoni punimet
          </a>
        </motion.div>

      </div>

      <motion.a
        href="#info"
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 1 }}
        className="absolute bottom-5 right-5 hidden text-white/40 transition-colors hover:text-bronze sm:block"
        aria-label="Zbrit poshtë"
      >
        <ArrowDown size={22} className="animate-bounce" />
      </motion.a>
    </section>
  );
};

export const InfoStrip = () => (
  <section id="info" data-testid="info-trust-strip" className="border-y hairline bg-graphite texture-brushed">
    <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/5 lg:grid-cols-4">
      {TRUST_ITEMS.map((item, i) => (
        <div key={i} data-testid={`trust-item-${i}`} className="flex flex-col items-start gap-2 px-6 py-8 sm:px-10 sm:py-10">
          <span className="font-display text-3xl font-bold text-bronze sm:text-4xl">{item.value}</span>
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/55">{item.label}</span>
        </div>
      ))}
    </div>
  </section>
);
